// @ts-nocheck
import { useState, useRef } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import omitEmpty from 'omit-empty';
import {
  makeStyles,
  Box,
  Grid,
  Icon,
  Button,
  Typography,
  Checkbox,
  Switch,
  FormControlLabel,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import PromotionForm from './PromotionForm';
import PromotionPeriodForm from './PromotionPeriodForm';
import PromotionConfirmation from './PromotionConfirmation';
import { useDialog } from '../../../../common/hooks';

const useStyles = makeStyles(theme => ({
  container: {
    flex: '1',
    display: 'flex',
    'flex-direction': 'column',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
  },
  button: {
    margin: theme.spacing(1),
    '&:last-child': {
      marginRight: '0',
    },
  },
  forms: {
    '& [id$="-actions"]': {
      display: 'none',
    },
  },
  includePeriod: {
    padding: theme.spacing(1, 3),
  },
}));

function PromotionCreateForm(props) {
  const [t] = useTranslation();
  const classes = useStyles();
  const [promotionId, setPromotionId] = useState(0);
  const [includePeriod, setIncludePeriod] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showDialog } = useDialog();

  const {
    products,
    onCreatePromotion,
    onCreatePromotionPeriod,
    onCancel,
    onFinish,
    editMode,
    actionsDisabled,
    cancelButtonText,
    cancelButtonIcon,
    saveButtonText,
    saveButtonIcon,
    getTypeDisplayValue,
  } = props;

  const promotionFormRef = useRef();
  const promotionPeriodFormRef = useRef();

  const onSubmitPromotion = async data => {
    try {
      const promo = await onCreatePromotion(data);
      setPromotionId(promo.data.id);
      if(promo.data.id !== 0 && includePeriod) {
        await promotionPeriodFormRef.current.submit();
      }
      onFinish();
    } catch (e) {
      // Error already handled
    }
  };

  const onSubmitPromotionPeriod = async data => {
    try {
      await onCreatePromotionPeriod(data);
      setPromotionId(0);
      onFinish();
    } catch (e) {
      // Error already handled
    }
  };

  const handleFormsSubmit = async () => {
    let result = promotionId !== 0;
    if (!result && promotionFormRef.current) {
      await promotionFormRef.current.submit();
    } else if (includePeriod) {
      await promotionPeriodFormRef.current.submit();
    }
  };

  const handleShowConfirmPromotionDialog = (promotion) => {
    return showDialog({
      title: t('promotion.promotionSummary'),
      content: params => (
        <Box className={classes.dialogContent}>
          <PromotionConfirmation
            promotion={promotion}
            onSave={onSubmitPromotion}
            onCancel={params.onCancel}
            getTypeDisplayValue={getTypeDisplayValue}
          />
        </Box>
      ),
      className: classes.dialog,
      throwOnError: true,
      hideFooter: true,
      keepPreviousOpen: true,
    });
  };

  return (
    <Box id="PromotionCreateForm-container" className={classes.container}>
      <Box>
        <Grid item xs={12} className={classes.forms}>
          <PromotionForm
            products={products}
            onSave={handleShowConfirmPromotionDialog}
            onCancel={onCancel}
            ref={promotionFormRef}
          />
        </Grid>
      </Box>
      
      <Box>
        <Grid item xs={12}>
          <FormControlLabel
            className={classes.includePeriod}
            control={
              <Switch onChange={() => setIncludePeriod(!includePeriod)}/>
            }
            label={t('promotion.promotionPeriodInclude')}
          />
        </Grid>

        {includePeriod && (
          <Grid item xs={12} className={classes.forms}>
            <PromotionPeriodForm 
              promotionId={promotionId}
              onSave={onSubmitPromotionPeriod}
              onCancel={onCancel}
              ref={promotionPeriodFormRef}
            />
          </Grid>
        )}
      </Box>

      <Box className={classes.actions}>
          <Button
            id="PromotionForm-cancel"
            className={classes.button}
            startIcon={<Icon>{'cancel'}</Icon>}
            onClick={onCancel}
            disabled={actionsDisabled || isSubmitting}
          >
            <Typography variant="button" noWrap>
              {t('common.cancel')}
            </Typography>
          </Button>
          <Button
            id="PromotionForm-save"
            key="save"
            onClick={handleFormsSubmit}
            color="primary"
            className={classes.button}
            startIcon={<Icon>{_.defaultTo(saveButtonIcon, 'save')}</Icon>}
            disabled={actionsDisabled || isSubmitting}
          >
            <Typography variant="button" noWrap>
              {_.defaultTo(saveButtonText, t('common.save'))}
            </Typography>
          </Button>
        </Box>
    </Box>
  );
}

PromotionCreateForm.propTypes = {
  onCreatePromotion: PropTypes.func.isRequired,
  onCreatePromotionPeriod: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  actionsDisabled: PropTypes.bool,
  editMode: PropTypes.bool,
  getTypeDisplayValue: PropTypes.func.isRequired,
};

export default PromotionCreateForm;
